import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { readInventoryQuery, buildInventoryQuery } from '../utils/inventoryQuery';

export default function PhotographerInventory() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  // ⚠ THE SELECTION IS SEEDED FROM THE URL, NOT FROM A DEFAULT (fixed 2026-10-08).
  // Opening a car navigates away, which UNMOUNTS this component and discards every
  // useState below -- so "Back to inventory" used to drop an admin back on "Browse by
  // dealership" and force her to re-pick the photographer after looking at ONE car
  // (Kim). The dealership, search, filter and sort were lost the same way.
  // Read ONCE through a ref: later URL writes must not re-seed state mid-session.
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = React.useRef(readInventoryQuery(searchParams)).current;

  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState(initial.dealerId);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState(initial.search);
  const [filter, setFilter] = useState(initial.filter);
  const [sort, setSort] = useState(initial.sort);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ⚠️ THE LIST USED TO SHOW 100 CARS AND CLAIM NOTHING (found 2026-09-30). The endpoint
  // defaults to limit=100, the response has ALWAYS carried `total`, and this page threw it
  // away -- so a rooftop with more than 100 active vehicles silently hid the rest with no
  // paging control and no count. Kim reported "photos are not visible in the portal": they
  // were uploading fine and the per-car page had them, but Brandon's cars were beyond car
  // 100 of Tuscaloosa's 307, so browsing could never reach them. Woody Naperville holds 689.
  // The count is now always shown, so the page can never again look complete when it is not.
  const [total, setTotal] = useState(0);
  // ⚠⚠ AND OFFSET PAGING CANNOT BE USED HERE AT ALL. My first fix added a "Load more"
  // button and it silently SKIPPED 9 of Tuscaloosa's 307 cars -- one of them Brandon's
  // 13306HA, which has 63 photos. The default sort is `created_at DESC` with NO unique
  // tiebreaker, and 293 of those 307 rows share the identical created_at from one bulk
  // import, so Postgres may order equal keys differently per query and LIMIT/OFFSET then
  // drops and repeats rows. Measured: the union of four 100-row pages = 298 distinct cars
  // against 307 in a single request.
  //
  // So this fetches the whole list in ONE request, which has no offset and therefore no
  // instability (verified: 307/307 distinct here, 689/689 at Woody Naperville, the largest
  // rooftop). It is also less code than paging.
  //
  // ⚠ The real defect is in the endpoint's ORDER BY and it affects the MOBILE APP TOO,
  // which pages at 30 -- a photographer scrolling a 307-car rooftop can never see some
  // cars. That needs a unique tiebreaker on every sort clause in get_mobile_vehicles, which
  // is a backend change and a dms-api restart, so it is NOT done here.
  //
  // LIMIT is generous rather than unbounded, and if it is ever hit the count line says so
  // instead of quietly truncating -- which is the whole failure being fixed.
  const FETCH_LIMIT = 2000;

  // by-photographer mode
  const [mode, setMode] = useState(initial.mode); // 'dealer' | 'photographer'
  const [photographers, setPhotographers] = useState([]); // admin dropdown
  const [photographerId, setPhotographerId] = useState(initial.photographerId);

  useEffect(() => {
    api.getAssignedDealerships()
      .then((d) => {
        setDealers(d || []);
        // Only auto-pick when the URL did not already say which view to restore.
        if (d && d.length === 1 && !initial.dealerId && initial.mode === 'dealer') setDealerId(d[0].id);
      })
      .catch((e) => setError(e.message));
    if (isAdmin) {
      api.getPhotographers().then((p) => setPhotographers(p || [])).catch(() => {});
    }
  }, [isAdmin]);

  const load = useCallback(() => {
    setError('');
    if (mode === 'photographer') {
      setLoading(true);
      api.getShotVehicles(isAdmin ? photographerId : null)
        .then((v) => setVehicles(Array.isArray(v) ? v : []))
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
      return;
    }
    // No dealership selected: search spans ALL the caller's dealerships (also
    // matches dealership names). Previously this state silently showed nothing,
    // which read as "search is broken" (Kim, 2026-07-22).
    if (!dealerId) {
      const q = search.trim();
      if (q.length < 2) { setVehicles([]); return; }
      setLoading(true);
      api.searchAllVehicles(q)
        .then((v) => setVehicles(Array.isArray(v) ? v : []))
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    api.getPhotographerVehicles(dealerId, { search, filter, limit: FETCH_LIMIT })
      .then((v) => {
        const rows = Array.isArray(v) ? v : (v.vehicles || []);
        setVehicles(rows);
        // `total` is what the dealership actually holds, for the count line below.
        setTotal(Array.isArray(v) ? rows.length : (v.total ?? rows.length));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [mode, dealerId, search, filter, isAdmin, photographerId]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // The address bar is what survives the unmount, so it has to reflect the live selection.
  // `replace` deliberately: a dropdown change is not a new place, and pushing one history
  // entry per keystroke would make the browser Back button useless.
  const viewQuery = buildInventoryQuery({ mode, photographerId, dealerId, search, filter, sort });
  // ⚠⚠ THE WRITE MUST BE GUARDED OR THIS EFFECT FEEDS ITSELF. react-router builds
  // `setSearchParams` with useCallback([navigate, searchParams]) and memoizes
  // `searchParams` on location.search, so WRITING the URL hands back a NEW
  // setSearchParams identity and re-fires this effect. Unguarded it terminates only
  // because of a useMemo detail inside the library -- not something to depend on.
  //
  // Both sides of the comparison are run through buildInventoryQuery, so they are in the
  // same canonical form and cannot disagree over key order or percent-encoding. A
  // comparison that could disagree would make this a RENDER LOOP rather than a no-op,
  // which is why it is normalised rather than compared as a raw string.
  // scripts/test_inventory_query_loop.js proves that fixed point, and the naive raw-string
  // comparison is demonstrably NOT one: a pasted ?q=a%20b normalises to q=a+b.
  const urlQuery = buildInventoryQuery(readInventoryQuery(searchParams));
  useEffect(() => {
    if (urlQuery === viewQuery) return;
    setSearchParams(viewQuery, { replace: true });
  }, [viewQuery, urlQuery, setSearchParams]);

  // Kim asked to come back "to a vehicle in the list", not to the top of it. The clicked
  // VIN is remembered rather than a pixel offset, because the list can render at a
  // different height on the way back (a photo finished uploading, a filter re-ran).
  useEffect(() => {
    if (loading || !vehicles.length) return;
    let vin = null;
    try { vin = sessionStorage.getItem(RETURN_VIN_KEY); } catch (e) { /* private mode */ }
    if (!vin) return;
    try { sessionStorage.removeItem(RETURN_VIN_KEY); } catch (e) { /* ignore */ }
    const el = document.getElementById(`veh-${vin}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' });
  }, [loading, vehicles]);

  // In photographer ("my shots") mode the backend returns newest-shot-first and
  // the search box filters the loaded list client-side (stock/VIN/make/dealer).
  const shotFiltered = mode === 'photographer' && search.trim()
    ? vehicles.filter((v) => {
        const q = search.trim().toLowerCase();
        return [v.stock_number, v.vin, v.make, v.model, v.dealership_name,
                `${v.year} ${v.make} ${v.model}`]
          .some((f) => f && String(f).toLowerCase().includes(q));
      })
    : vehicles;
  const sorted = mode === 'photographer' ? shotFiltered : [...vehicles].sort((a, b) => {
    if (sort === 'photos') return (a.photo_count || 0) - (b.photo_count || 0);
    if (sort === 'year') return (b.year || 0) - (a.year || 0);
    return 0;
  });

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Vehicle Assets</h2>
        <div>
          <span style={{ color: '#6b7280', marginRight: 12 }}>{user?.email}{isAdmin ? ' (admin)' : ''}</span>
          <button onClick={() => { logout(); navigate('/photographer/login'); }} style={linkBtn}>Sign out</button>
        </div>
      </div>
      {error && <div style={{ color: '#dc2626' }}>{error}</div>}

      {/* Mode toggle: browse by dealership vs by photographer */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '12px 0', alignItems: 'center' }}>
        {isAdmin ? (
          <select
            value={mode === 'photographer' ? (photographerId || '') : ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val) { setPhotographerId(Number(val)); setMode('photographer'); }
              else { setMode('dealer'); setPhotographerId(null); }
            }}
            style={ctrl}
            title="Filter by photographer"
          >
            <option value="">Browse by dealership</option>
            {photographers.map((p) => (
              <option key={p.id} value={p.id}>📷 {p.name || p.email}</option>
            ))}
          </select>
        ) : (
          <label style={{ ...ctrl, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={mode === 'photographer'}
              onChange={(e) => setMode(e.target.checked ? 'photographer' : 'dealer')}
            />
            Show only my shots (newest first)
          </label>
        )}

        {mode === 'dealer' && (
          <select value={dealerId || ''} onChange={(e) => setDealerId(Number(e.target.value) || null)} style={ctrl}>
            <option value="">All dealerships (search)</option>
            {dealers.map((d) => <option key={d.id} value={d.id}>{d.dealership_name} ({d.dealer_code})</option>)}
          </select>
        )}
        {/* Search is always available: scoped to the selected dealership, across
            ALL dealerships when none is selected, or filtering "my shots". */}
        <input
          placeholder={mode === 'photographer' ? 'Filter my shots…'
            : dealerId ? 'Search VIN / stock / make…'
            : 'Search all dealerships — VIN / stock / make / dealer…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...ctrl, minWidth: 260 }}
        />
        {mode === 'dealer' && dealerId && (
          <>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} style={ctrl}>
              <option value="">All</option>
              <option value="needs_photos">Needs photos</option>
              <option value="complete">Complete</option>
              <option value="in_progress">In progress</option>
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} style={ctrl}>
              <option value="default">Sort</option>
              <option value="photos">Fewest photos</option>
              <option value="year">Newest year</option>
            </select>
          </>
        )}
      </div>

      {/* Say how many of how many, always. A page that shows 100 of 307 and says nothing is
          indistinguishable from a rooftop that only has 100 cars. */}
      {!loading && mode === 'dealer' && dealerId && total > 0 && (
        <div style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 10px' }}>
          Showing {sorted.length} of {total}
          {vehicles.length < total && !search.trim() && (
            <span style={{ color: '#b45309' }}>
              {' '}— {total - vehicles.length} not shown, search to find a specific car
            </span>
          )}
        </div>
      )}
      {loading ? <div>Loading…</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 14 }}>
          {sorted.map((v) => {
            const navDealer = v.dealer_id || dealerId; // by-photographer rows carry their own dealer id
            return (
              <div
                key={`${navDealer}-${v.vin}`}
                id={`veh-${v.vin}`}
                onClick={() => {
                  try { sessionStorage.setItem(RETURN_VIN_KEY, v.vin); } catch (e) { /* private mode */ }
                  navigate(`/photographer/vehicle/${navDealer}/${v.vin}${viewQuery ? `?${viewQuery}` : ''}`);
                }}
                style={card}
              >
                <div style={{ position: 'relative' }}>
                  {v.thumbnail_url
                    ? <img src={v.thumbnail_url} alt="" style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 6 }} />
                    : <div style={noImg}>No photo</div>}
                  {v.shoot_blocked_reason && (
                    <div style={blockedBadge}>🚫 {v.shoot_blocked_reason}</div>
                  )}
                </div>
                <div style={{ fontWeight: 600, marginTop: 8 }}>{v.year} {v.make} {v.model}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{v.trim}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Stock #{v.stock_number || '—'}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>VIN {v.vin}</div>
                {v.dealership_name && (
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{v.dealership_name}</div>
                )}
                <div style={{ fontSize: 12, marginTop: 4 }}>📷 {v.photo_count ?? 0} · 🎬 {v.video_count ?? 0}</div>
              </div>
            );
          })}
          {!sorted.length && !loading && mode === 'dealer' && dealerId && (
            <div style={{ color: '#6b7280' }}>{search.trim() ? 'No matches in this dealership — clear the dealership to search all of yours.' : 'No vehicles.'}</div>
          )}
          {!sorted.length && !loading && mode === 'dealer' && !dealerId && (
            <div style={{ color: '#6b7280' }}>
              {search.trim().length >= 2
                ? 'No matches across your dealerships.'
                : 'Select a dealership, or type in the search box to search across all of your dealerships.'}
            </div>
          )}
          {!sorted.length && !loading && mode === 'photographer' && (
            <div style={{ color: '#6b7280' }}>{search.trim() ? 'No shots match your filter.' : 'No shot vehicles yet (attribution starts 2026-06-02).'}</div>
          )}
        </div>
      )}

    </div>
  );
}

// Scoped to this tab, and cleared the moment it is used, so it can never silently
// yank a later visit to a car she did not click.
const RETURN_VIN_KEY = 'phInvReturnVin';

const ctrl = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6 };
const card = { border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, cursor: 'pointer', background: '#fff' };
const noImg = { height: 150, background: '#f1f5f9', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' };
const blockedBadge = { position: 'absolute', top: 6, left: 6, background: 'rgba(208,2,27,0.92)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 7px', borderRadius: 5 };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer' };
